/**
 * ShippingAgentSettings view component.
 * @module components/theme/ShippingAgentSettings
 */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { Link, useParams } from 'react-router-dom';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import {
  Container as SemanticContainer,
  Grid,
  Label,
  Segment,
} from 'semantic-ui-react';
import config from '@plone/volto/registry';
import { getContent } from '@plone/volto/actions/content/content';
import { getWidget } from '@plone/volto/helpers/Widget/utils';
import { useClient } from '@plone/volto/hooks/client/useClient';
import { getParentUrl } from '@plone/volto/helpers/Url/Url';
import { Helmet } from '@plone/volto/helpers/Helmet/Helmet';
import { default as DefaultView } from '@plone/volto/components/theme/View/DefaultView';
import { default as Icon } from '@plone/volto/components/theme/Icon/Icon';
import { default as Toolbar } from '@plone/volto/components/manage/Toolbar/Toolbar';
import Unauthorized from '@plone/volto/components/theme/Unauthorized/Unauthorized';
import backSVG from '@plone/volto/icons/back.svg';
import penSVG from '@plone/volto/icons/pen.svg';
import {
  clearShippingAgentSettings,
  getShippingAgentSettings,
} from '../../../actions/shop/shipping_agents_settings';

const messages = defineMessages({
  articles: {
    id: 'articles',
    defaultMessage: 'Articles',
  },
  back: {
    id: 'back',
    defaultMessage: 'Back',
  },
  calendar: {
    id: 'calendar',
    defaultMessage: 'Calendar',
  },
  edit: {
    id: 'edit',
    defaultMessage: 'Edit',
  },
  otherChanges: {
    id: 'Make other changes',
    defaultMessage:
      'Should there be other configurations you need to do, but you can not do them ' +
      'through this application, please contact Apanymantel as usual or send an ' +
      'e-mail to info@apanymantel.com.',
  },
  orders: {
    id: 'orders',
    defaultMessage: 'Orders',
  },
  prices: {
    id: 'prices',
    defaultMessage: 'Prices',
  },
  shippingAgentSettings: {
    id: 'shippingAgentSettings',
    defaultMessage: 'Shipping Agent Settings',
  },
  shop: {
    id: 'shop',
    defaultMessage: 'Shop',
  },
});
/**
 * ShopView view component class.
 * @function ShopView
 * @params {object} content Content object.
 * @return {string} Markup of the component .
 */
const ShippingAgentSettings = ({ location }) => {
  const { pathname } = location;
  const { id } = useParams();

  const intl = useIntl();
  const dispatch = useDispatch();
  const isClient = useClient();

  const { views } = config.widgets;
  const contentUrl = getParentUrl(getParentUrl(pathname));
  const shippingAgent = useSelector(
    (state) => state.shippingAgentsSettings?.shippingAgentSettings,
  );
  const title = useSelector((state) => state.content?.data?.title || '');
  const statusError = useSelector(
    (state) => state.shippingAgentsSettings?.get?.error?.status,
  );
  useEffect(() => {
    if (!title) {
      dispatch(getContent(contentUrl));
    }
  }, [title]);

  useEffect(() => {
    dispatch(getShippingAgentSettings(contentUrl, id));
    return () => dispatch(clearShippingAgentSettings());
  }, [pathname, dispatch, contentUrl, id]);

  const contentSchema = useSelector(
    (state) => state.shippingAgentsSettings?.shippingAgentSettings?.schema,
  );
  const fieldsets = contentSchema?.fieldsets;

  const Container =
    config.getComponent({ name: 'Container' }).component || SemanticContainer;

  if (isClient && statusError === 401) {
    return <Unauthorized />;
  }

  return (
    <Container id="page-shipping-agent">
      <Helmet title={intl.formatMessage(messages.shippingAgentSettings)} />
      <Segment.Group raised>
        <Segment className="primary">
          <FormattedMessage
            id="Shipping Agent Settings of {shippingAgentTitle} for {title}"
            defaultMessage="Shipping Agent Settings of {shippingAgentTitle} for {title}"
            values={{
              title: <q>{title}</q>,
              shippingAgentTitle: shippingAgent?.title || '',
            }}
          />
          {fieldsets?.map((fs) => {
            return (
              <div className="fieldset" key={fs.id}>
                {fs.id !== 'default' && <h2>{fs.title}</h2>}
                {fs.fields?.map((f, key) => {
                  let field = {
                    ...contentSchema?.properties[f],
                    id: f,
                    widget: getWidget(f, contentSchema?.properties[f]),
                  };
                  let Widget = views?.getWidget(field);
                  return (
                    <Grid celled="internally" key={key}>
                      <Grid.Row>
                        <Label title={field.id}>{field.title}:</Label>
                      </Grid.Row>
                      <Grid.Row>
                        <Segment basic>
                          <Widget value={shippingAgent.data[f]} />
                        </Segment>
                      </Grid.Row>
                    </Grid>
                  );
                })}
              </div>
            );
          })}
        </Segment>
      </Segment.Group>
      {isClient &&
        createPortal(
          <Toolbar
            pathname={pathname}
            hideDefaultViewButtons
            inner={
              <>
                <Link
                  to={`${contentUrl}/shipping-agent-settings/${id}/edit`}
                  className="edit"
                >
                  <Icon
                    name={penSVG}
                    className="circled"
                    size="30px"
                    title={intl.formatMessage(messages.edit)}
                  />
                </Link>
                <Link
                  to={`${contentUrl}/shipping-agents-settings`}
                  className="item"
                >
                  <Icon
                    name={backSVG}
                    className="contents circled"
                    size="30px"
                    title={intl.formatMessage(messages.back)}
                  />
                </Link>
              </>
            }
          />,
          document.getElementById('toolbar'),
        )}
    </Container>
  );
  return (
    <Container id="page-document">
      {fieldsets?.map((fs) => {
        return (
          <div className="fieldset" key={fs.id}>
            {fs.id !== 'default' && <h2>{fs.title}</h2>}
            {fs.fields?.map((f, key) => {
              let field = {
                ...contentSchema?.properties[f],
                id: f,
                widget: getWidget(f, contentSchema?.properties[f]),
              };
              let Widget = views?.getWidget(field);
              return f !== 'title' ? (
                <Grid celled="internally" key={key}>
                  <Grid.Row>
                    <Label title={field.id}>{field.title}:</Label>
                  </Grid.Row>
                  <Grid.Row>
                    <Segment basic>
                      <Widget value={content[f]} />
                    </Segment>
                  </Grid.Row>
                </Grid>
              ) : (
                <Widget key={key} value={content[f]} />
              );
            })}
          </div>
        );
      })}
    </Container>
  );
};

/**
 * Property Types
 * @property {Object} propTypes Property types.
 * @static
 */
ShippingAgentSettings.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }).isRequired,
};

export default ShippingAgentSettings;
